import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { CircularProgress } from '@mui/material';
import styled, { css } from 'styled-components';
import { useLocation } from 'react-router-dom';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { useQueryClient } from '@tanstack/react-query';
import useAiQuota from '../../../hooks/useAiQuota';
import useAiChatModels from '../../../hooks/useAiChatModels';
import usePrevious from '../../../hooks/usePrevious';
import { AGENT_SESSIONS } from '../../../constants/queryKeys';
import { Flex, FlexColumn } from '../../Flex';
import IconButton from '../../IconButton';
import TextField from '../../TextField';
import {
  Announcement,
  ChatIntegration,
  ChatMessage as ChatMessageType,
  ChatMode,
  ChatStarter,
  QuotaScope,
  Suggestion,
  ToolRendererFunction,
} from '../types';
import useSessionStream from '../hooks/useSessionStream';
import { TrackingContext } from '../../../providers/tracking';
import { AgentContext, AgentOrigin } from '../../../types';
import {
  ANNOUNCEMENTS,
  SHOW_ANNOUNCEMENTS_ONLY_IN_EMPTY_STATE,
} from '../constants';
import {
  fontSize,
  radius,
  shadow,
  space,
  componentToken,
} from '../../../style/utils';
import AnnouncementsBar from './AnnouncementsBar';
import ContextChips from './ContextChips';
import EnvDisplayChip from './EnvDisplayChip';
import IntegrationsPickerButton from './IntegrationsPickerButton';
import ModeSelector from './ModeSelector';
import ModelMenuSelector from './ModelMenuSelector';
import EmptyState from './EmptyState';
import StarterMessage from './StarterMessage';
import SuggestionSection from './SuggestionSection';
import ChatMessage from './ChatMessage';
import QuotaBanner, { buildQuotaMessage } from './QuotaBanner';
import StuckMessage from './StuckMessage';
import AssistantStatusIndicator from './AssistantStatusIndicator';
import { shouldShowAssistantWorking } from './messageParts';
// for debugging, allows us to:
// - see the tool invocations and results in the UI
// - control the model used for the chat
const isDebug = false;
const QUOTA_EXHAUSTED_ERROR_CODE = 'quota_exhausted';

const Wrapper = styled(FlexColumn)<{ $isEmpty?: boolean }>`
  height: 100%;
  justify-content: ${({ $isEmpty }) => ($isEmpty ? 'center' : 'space-between')};
  ${({ $isEmpty }) => $isEmpty && 'gap: 36px;'}
  position: relative;
`;

const FLOATING_FOOTER_CLEARANCE = 120;

const MessageSection = styled(FlexColumn)<{
  $noPadding?: boolean;
  $contentMaxWidth?: number;
  $bottomPadding: number;
}>`
  gap: ${space('lg')};
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ $noPadding, $bottomPadding }) =>
    $noPadding ? 0 : `24px 24px ${$bottomPadding}px 24px`};
  ${({ $contentMaxWidth }) =>
    $contentMaxWidth && 'container-type: inline-size;'}
`;

const Footer = styled(Flex)<{
  $noPadding?: boolean;
  $maxWidth?: number;
  $floating?: boolean;
}>`
  padding: ${({ $noPadding }) =>
    $noPadding ? 0 : space(0, 'xxl', 'xxl', 'xxl')};
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : 'none')};
  width: 100%;
  align-self: center;
  ${({ $floating, theme }) =>
    $floating &&
    `
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    padding-top: ${theme.tokens.space.xxl};
    background: linear-gradient(
      to bottom,
      transparent 0,
      ${theme.palette.surface.soft} 24px
    );
  `}
`;

const borderGradient = css`
  border: 1px solid transparent !important;
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: ${shadow('smMedium')};
  background-image: linear-gradient(
      ${({ theme }) => theme.palette.surface.base},
      ${({ theme }) => theme.palette.surface.base}
    ),
    linear-gradient(
      to right,
      ${({ theme }) => theme.palette.accent.aiGradient.start},
      ${({ theme }) => theme.palette.accent.aiGradient.middle},
      ${({ theme }) => theme.palette.accent.aiGradient.end}
    );
`;

const askGradient = css`
  border: 1px solid transparent !important;
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: ${shadow('smSoft')};
  background-image: linear-gradient(
      ${({ theme }) => theme.palette.surface.base},
      ${({ theme }) => theme.palette.surface.base}
    ),
    linear-gradient(
      to right,
      ${componentToken('aiChat.askGradient.start')},
      ${componentToken('aiChat.askGradient.end')}
    );
`;

const FooterContent = styled(FlexColumn)`
  width: 100%;
  position: relative;
`;

const AnnouncementsBarLayer = styled(Flex)`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  justify-content: center;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const InputContainer = styled(FlexColumn)`
  width: 100%;
  border-radius: ${radius('sm')};
  ${borderGradient}
`;

const EnvRow = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: ${space('sm', 'xs', 0)};
`;

const TextFieldWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const TextFieldStyled = styled(TextField)`
  .MuiInputBase-root {
    font-size: ${fontSize('base')};
  }

  .MuiInputBase-input {
    padding-right: ${space('mdMinus')};
  }

  .MuiOutlinedInput-root {
    border: none !important;
    box-shadow: none !important;
    background: none !important;
    background-image: none !important;
    .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
    :hover .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
  }
`;

const SubmitArea = styled(Flex)`
  gap: ${space('xs')};
  align-items: center;
  justify-content: flex-end;
  padding: ${space('xs', 'mdMinus', 'mdMinus')};
`;

const SubmitButton = styled(IconButton)<{ $mode: ChatMode }>`
  ${({ $mode }) => ($mode === 'ask' ? askGradient : borderGradient)}

  :hover {
    background-image: linear-gradient(
        ${componentToken('aiChat.submitHoverBg')},
        ${componentToken('aiChat.submitHoverBg')}
      ),
      ${({ $mode, theme }) =>
        $mode === 'ask'
          ? `linear-gradient(to right, ${theme.tokens.components.aiChat.askGradient.start}, ${theme.tokens.components.aiChat.askGradient.end})`
          : `linear-gradient(to right, ${theme.palette.accent.aiGradient.start}, ${theme.palette.accent.aiGradient.middle}, ${theme.palette.accent.aiGradient.end})`} !important;
  }
`;

type Props = {
  placeholder?: string;
  startMessage?: string;
  openOrigin?: AgentOrigin;
  noPadding?: boolean;
  title?: string;
  starter?: ChatStarter;
  suggestions?: Suggestion[];
  chatHistory?: {
    threadId: string;
    messages: ChatMessageType[];
  };
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  context?: AgentContext;
  onContextChange?: (context: AgentContext | undefined) => void;
  onEmptyStateChange?: (isEmpty: boolean) => void;
  onThreadIdChange?: (threadId: string) => void;
  contentMaxWidth?: number;
  availableModes?: ChatMode[];
  mode?: ChatMode;
  onModeChange?: (mode: ChatMode) => void;
  enabledIntegrations?: ChatIntegration[];
  onEnabledIntegrationsChange?: (values: ChatIntegration[]) => void;
  onBlockedAction?: (message: string) => void;
  focusToolCallId?: string;
  showToolErrorDetails?: boolean;
  contextualAnnouncements?: Announcement[];
  readOnly?: boolean;
};

const AiChat = ({
  placeholder = 'Ask a question...',
  startMessage,
  openOrigin,
  noPadding,
  title = 'What are we working on today?',
  starter,
  suggestions,
  chatHistory,
  getToolRenderer,
  context,
  onContextChange,
  onEmptyStateChange,
  onThreadIdChange,
  contentMaxWidth,
  availableModes = ['agent'],
  mode: selectedMode = 'agent',
  onModeChange: setSelectedMode,
  enabledIntegrations,
  onEnabledIntegrationsChange,
  onBlockedAction,
  focusToolCallId,
  showToolErrorDetails = false,
  contextualAnnouncements = [],
  readOnly = false,
}: Props) => {
  const queryClient = useQueryClient();
  const tracking = useContext(TrackingContext);
  const { pathname } = useLocation();
  const { data: sessionUsage, invalidateQuotaQuery } = useAiQuota();
  const monthlyLimitReached = !!sessionUsage?.monthly_limit_reached;
  const dailyLimitReached = !!sessionUsage?.exhausted;
  const quotaScope: QuotaScope = monthlyLimitReached ? 'monthly' : 'daily';
  const quotaResetsAt = monthlyLimitReached
    ? sessionUsage?.monthly_resets_at
    : sessionUsage?.resets_at;
  const quotaMessage = buildQuotaMessage(quotaScope, quotaResetsAt);
  const notifyBlocked = useCallback(() => {
    onBlockedAction?.(quotaMessage);
  }, [onBlockedAction, quotaMessage]);
  const { modelGroups, selectedModel, setSelectedModel, isByokConfigured } =
    useAiChatModels();
  const [userScrolled, setUserScrolled] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [inputValue, setInputValue] = useState<string>('');
  const [startMessageSent, setStartMessageSent] = useState(false);
  const [shouldInvalidateHistory, setShouldInvalidateHistory] = useState(true);
  const [textIndent, setTextIndent] = useState(0);
  const [floatingFooterHeight, setFloatingFooterHeight] = useState(0);
  const [isContextAttached, setIsContextAttached] = useState(true);

  const bottomRef = useRef<HTMLDivElement>(null);
  const messageSectionRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipOverlayRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLElement | null>(null);
  const scrolledForIdRef = useRef<string | undefined>(undefined);

  const entityContextKey = useMemo(
    () =>
      JSON.stringify({
        assetIds: context?.assetIds,
        testIds: context?.testIds,
        incidentIds: context?.incidentIds,
        displayLabels: context?.displayLabels,
      }),
    [
      context?.assetIds,
      context?.testIds,
      context?.incidentIds,
      context?.displayLabels,
    ],
  );
  const prevEntityContextKeyRef = useRef(entityContextKey);

  useEffect(() => {
    if (prevEntityContextKeyRef.current === entityContextKey) return;
    prevEntityContextKeyRef.current = entityContextKey;
    setIsContextAttached(true);
  }, [entityContextKey]);

  const messageContext = useMemo(() => {
    if (isContextAttached || !context) return context;
    const envIds = context.environmentIds;
    return envIds?.length ? { environmentIds: envIds } : undefined;
  }, [context, isContextAttached]);

  const contextRef = useRef(messageContext);
  contextRef.current = messageContext;

  const {
    threadId,
    consolidatedMessages: messages,
    sendMessage,
    stop,
    isLoading,
    continueChat,
    approveToolUse,
    applyFrontendTool,
    rejectFrontendTool,
    userInteraction,
    error,
    errorCode,
    liveQuotaExhausted,
    isInitializing,
    canSend,
    canRespondToInput,
  } = useSessionStream({
    chatHistory,
    context: messageContext,
    mode: selectedMode,
    selectedModel,
    enabledIntegrations,
    enabled: !readOnly,
  });

  useEffect(() => {
    if (errorCode === QUOTA_EXHAUSTED_ERROR_CODE) {
      invalidateQuotaQuery();
    }
  }, [errorCode, invalidateQuotaQuery]);
  // A BYOK account bills its own provider directly, so the backend bypasses
  // Elementary quota for every model — never block the UI on quota here.
  const quotaExhausted =
    (monthlyLimitReached || dailyLimitReached || liveQuotaExhausted) &&
    !isByokConfigured;

  const showWorking = shouldShowAssistantWorking(messages, isLoading);
  const isAgentActive = isLoading || showWorking;

  useEffect(() => {
    onThreadIdChange?.(threadId);
  }, [threadId, onThreadIdChange]);

  const isEmpty = messages.length === 0;
  const showStarter = isEmpty && !isInitializing && !!starter;
  const isCenteredEmpty = isEmpty && !showStarter;
  const showAnnouncements = SHOW_ANNOUNCEMENTS_ONLY_IN_EMPTY_STATE
    ? isCenteredEmpty
    : true;

  useEffect(() => {
    onEmptyStateChange?.(isEmpty);
  }, [isEmpty, onEmptyStateChange]);

  const prevIsLoading = usePrevious(isLoading);
  const prevMessages = usePrevious(messages);

  const chatMessageMaxWidth = useMemo(
    () => (contentMaxWidth ? contentMaxWidth - 60 : undefined),
    [contentMaxWidth],
  );

  const bottomPadding = useMemo(
    () =>
      contentMaxWidth
        ? Math.max(floatingFooterHeight + 24, FLOATING_FOOTER_CLEARANCE)
        : 0,
    [contentMaxWidth, floatingFooterHeight],
  );

  const clearContextKeepEnvs = useCallback(() => {
    if (!context) return;
    const envIds = context.environmentIds;
    if (envIds && envIds.length > 0) {
      onContextChange?.({ environmentIds: envIds });
    } else {
      onContextChange?.(undefined);
    }
  }, [context, onContextChange]);

  const handleMessageSubmit = useCallback(() => {
    if (!inputValue.trim()) return;
    if (!canSend) return;
    if (quotaExhausted) {
      notifyBlocked();
      return;
    }
    setShouldAutoScroll(true);
    setUserScrolled(false);
    tracking?.sendTrackingEvent({
      eventName: tracking.analytics.events.AI_CHAT_MESSAGE_SENT,
      properties: { source_page: pathname },
    });
    sendMessage(inputValue, messageContext, 'free_text');
    setInputValue('');
    setIsContextAttached(false);
  }, [
    inputValue,
    canSend,
    sendMessage,
    messageContext,
    quotaExhausted,
    notifyBlocked,
    tracking,
    pathname,
  ]);

  const handleSuggestionSelect = useCallback(
    (value: string, origin: AgentOrigin = 'conversation_starter') => {
      if (!canSend) return;
      if (quotaExhausted) {
        notifyBlocked();
        return;
      }
      tracking?.sendTrackingEvent({
        eventName: tracking.analytics.events.AI_CHAT_MESSAGE_SENT,
        properties: { source_page: pathname },
      });
      sendMessage(value, messageContext, origin);
      setIsContextAttached(false);
    },
    [
      canSend,
      sendMessage,
      messageContext,
      quotaExhausted,
      notifyBlocked,
      tracking,
      pathname,
    ],
  );

  const handleStarterAction = useCallback(
    (prompt: string) => handleSuggestionSelect(prompt, openOrigin),
    [handleSuggestionSelect, openOrigin],
  );

  useEffect(() => {
    if (prevIsLoading && !isLoading) {
      invalidateQuotaQuery();
    }
  }, [isLoading, prevIsLoading, invalidateQuotaQuery]);

  useEffect(() => {
    if (
      startMessage &&
      !isLoading &&
      !startMessageSent &&
      !quotaExhausted &&
      canSend
    ) {
      sendMessage(startMessage, contextRef.current, openOrigin);
      setStartMessageSent(true);
      setIsContextAttached(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMessage, isLoading, quotaExhausted, canSend]);

  useEffect(() => {
    const clearHighlight = () => {
      if (highlightedRef.current) {
        highlightedRef.current.classList.remove('ai-chat-tool-call-highlight');
        highlightedRef.current = null;
      }
    };

    if (!focusToolCallId) {
      clearHighlight();
      scrolledForIdRef.current = undefined;
      return;
    }

    const isPending = messages.some((message) =>
      (message.parts ?? []).some(
        (part) =>
          part.type === 'tool-invocation' &&
          part.toolInvocation.state === 'call' &&
          part.toolInvocation.type === 'approval' &&
          part.toolInvocation.toolCallId === focusToolCallId,
      ),
    );

    const messageSection = messageSectionRef.current;
    const target = messageSection
      ? Array.from(
          messageSection.querySelectorAll<HTMLElement>('[data-tool-call-id]'),
        ).find((element) => element.dataset.toolCallId === focusToolCallId)
      : undefined;

    if (!isPending || !target) {
      clearHighlight();
      return;
    }

    if (highlightedRef.current !== target) {
      clearHighlight();
      target.classList.add('ai-chat-tool-call-highlight');
      highlightedRef.current = target;
    }

    if (scrolledForIdRef.current !== focusToolCallId) {
      scrolledForIdRef.current = focusToolCallId;
      setUserScrolled(true);
      setShouldAutoScroll(false);
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [messages, focusToolCallId]);

  useEffect(
    () => () => {
      if (highlightedRef.current) {
        highlightedRef.current.classList.remove('ai-chat-tool-call-highlight');
      }
    },
    [],
  );

  // Scroll to bottom when messages change, but only if user hasn't scrolled
  // or if they've scrolled back to the bottom
  useEffect(() => {
    if (bottomRef.current && shouldAutoScroll && !userScrolled) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldAutoScroll, userScrolled]);

  // Handle user scroll interaction
  useEffect(() => {
    const messageSection = messageSectionRef.current;
    if (!messageSection) return undefined;

    const handleScroll = () => {
      if (!messageSection) return;

      const isScrolledToBottom =
        messageSection.scrollHeight - messageSection.scrollTop <=
        messageSection.clientHeight + 50;

      // Only update userScrolled state if user has actually scrolled
      if (!isScrolledToBottom) {
        setUserScrolled(true);
        if (isLoading) {
          setShouldAutoScroll(false);
        }
      } else {
        setUserScrolled(false);
        setShouldAutoScroll(true);
      }
    };

    messageSection.addEventListener('scroll', handleScroll);
    return () => {
      messageSection.removeEventListener('scroll', handleScroll);
    };
  }, [isLoading]);

  // Track message updates during loading
  useEffect(() => {
    // When messages are updated during loading, maintain auto-scroll behavior
    // based on user's scroll position
    if (isLoading && prevMessages && messages.length !== prevMessages.length) {
      // Don't auto-scroll if user has manually scrolled up
      if (userScrolled) {
        setShouldAutoScroll(false);
      }
    }
  }, [isLoading, messages, prevMessages, userScrolled]);

  // Reset user scrolled state when sending a new message
  useEffect(() => {
    if (
      inputValue === '' &&
      messages.length > 0 &&
      messages[messages.length - 1].role === 'user'
    ) {
      setUserScrolled(false);
      setShouldAutoScroll(true);
    }
  }, [inputValue, messages]);

  useEffect(() => {
    if (prevIsLoading && !isLoading) {
      if (shouldInvalidateHistory) {
        setShouldInvalidateHistory(false);
        setTimeout(() => {
          queryClient.invalidateQueries([AGENT_SESSIONS]);
        }, 2000);
      }
      inputRef.current?.focus();

      // If user hasn't manually scrolled, scroll to bottom when loading finishes
      if (!userScrolled) {
        setShouldAutoScroll(true);
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, prevIsLoading, userScrolled]);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer || !contentMaxWidth) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setFloatingFooterHeight(entry.contentRect.height);
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, [contentMaxWidth]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea || !context) return undefined;

    const handleInputScroll = () => {
      if (chipOverlayRef.current) {
        chipOverlayRef.current.style.top = `${10 - textarea.scrollTop}px`;
      }
    };

    textarea.addEventListener('scroll', handleInputScroll);
    return () => textarea.removeEventListener('scroll', handleInputScroll);
  }, [context]);

  return (
    <Wrapper $isEmpty={isCenteredEmpty}>
      {isEmpty &&
        (isInitializing ? (
          <Flex sx={{ justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Flex>
        ) : starter ? (
          <MessageSection
            $noPadding={noPadding}
            $contentMaxWidth={contentMaxWidth}
            $bottomPadding={bottomPadding}
          >
            <StarterMessage
              message={starter.message}
              actions={starter.actions}
              disabled={!canSend}
              onSelectAction={handleStarterAction}
              maxWidth={chatMessageMaxWidth}
            />
          </MessageSection>
        ) : (
          <EmptyState title={title} maxWidth={contentMaxWidth} />
        ))}

      {!!messages.length && (
        <MessageSection
          ref={messageSectionRef}
          $noPadding={noPadding}
          $contentMaxWidth={contentMaxWidth}
          $bottomPadding={bottomPadding}
        >
          {messages.map((message, i) => {
            const isTurnComplete =
              message.role === 'assistant' &&
              (i < messages.length - 1 || !isAgentActive);
            return (
              <ChatMessage
                key={i}
                message={message}
                isDebug={isDebug}
                onApproveToolUse={approveToolUse}
                onApplyFrontendTool={readOnly ? undefined : applyFrontendTool}
                onRejectFrontendTool={readOnly ? undefined : rejectFrontendTool}
                onUserInteraction={userInteraction}
                interactionsDisabled={!canRespondToInput || readOnly}
                getToolRenderer={getToolRenderer}
                contentMaxWidth={chatMessageMaxWidth}
                showToolErrorDetails={showToolErrorDetails}
                isStreaming={isLoading && i === messages.length - 1}
                isTurnComplete={isTurnComplete}
              />
            );
          })}

          {error &&
            !isLoading &&
            !(errorCode === QUOTA_EXHAUSTED_ERROR_CODE && quotaExhausted) && (
              <StuckMessage
                onContinue={quotaExhausted ? notifyBlocked : continueChat}
              />
            )}

          <AssistantStatusIndicator
            status={showWorking ? 'working' : 'idle'}
            contentMaxWidth={chatMessageMaxWidth}
          />
          <div ref={bottomRef} />
        </MessageSection>
      )}

      <Footer
        ref={footerRef}
        $noPadding={noPadding}
        $maxWidth={
          contentMaxWidth && isCenteredEmpty
            ? Math.min(contentMaxWidth, 500)
            : contentMaxWidth
        }
        $floating={!!contentMaxWidth && !isCenteredEmpty}
      >
        <FooterContent>
          {quotaExhausted ? (
            <AnnouncementsBarLayer>
              <QuotaBanner scope={quotaScope} resetsAt={quotaResetsAt} />
            </AnnouncementsBarLayer>
          ) : (
            showAnnouncements && (
              <AnnouncementsBarLayer>
                <AnnouncementsBar
                  announcements={[...contextualAnnouncements, ...ANNOUNCEMENTS]}
                />
              </AnnouncementsBarLayer>
            )
          )}
          <InputContainer>
            <TextFieldWrapper>
              <ContextChips
                ref={chipOverlayRef}
                context={messageContext}
                onWidthChange={setTextIndent}
                onClear={clearContextKeepEnvs}
              />
              <TextFieldStyled
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={placeholder}
                multiline
                maxRows={17}
                fullWidth
                autoFocus
                inputRef={inputRef}
                sx={
                  textIndent > 0
                    ? {
                        '& .MuiInputBase-input': {
                          textIndent: `${textIndent}px`,
                        },
                      }
                    : undefined
                }
                onKeyDown={(e) => {
                  if (
                    e.key === 'Backspace' &&
                    !inputValue &&
                    isContextAttached &&
                    context
                  ) {
                    clearContextKeepEnvs();
                  }
                  if (
                    e.key === 'Enter' &&
                    !e.shiftKey &&
                    !e.nativeEvent.isComposing &&
                    !isAgentActive &&
                    canSend
                  ) {
                    e.preventDefault();
                    handleMessageSubmit();
                  }
                  if (
                    e.key.toLowerCase() === 'c' &&
                    e.ctrlKey &&
                    isAgentActive &&
                    inputRef.current?.selectionStart ===
                      inputRef.current?.selectionEnd
                  ) {
                    stop();
                  }
                }}
              />
            </TextFieldWrapper>
            <SubmitArea>
              <Flex sx={{ marginRight: 'auto', alignItems: 'center', gap: 1 }}>
                {setSelectedMode && (
                  <ModeSelector
                    availableModes={availableModes}
                    selectedMode={selectedMode}
                    onChange={setSelectedMode}
                  />
                )}
                <ModelMenuSelector
                  groups={modelGroups}
                  selectedModel={selectedModel}
                  onChange={setSelectedModel}
                />
              </Flex>
              <SubmitButton
                $mode={selectedMode}
                disabled={!isAgentActive && (!inputValue.trim() || !canSend)}
                onClick={isAgentActive ? stop : handleMessageSubmit}
                variant="outlined"
              >
                {isAgentActive ? (
                  <StopRoundedIcon
                    sx={{
                      fontSize: fontSize('xxl'),
                      color:
                        selectedMode === 'ask'
                          ? 'accent.secondary'
                          : 'accent.ai',
                    }}
                  />
                ) : (
                  <ArrowUpwardRoundedIcon
                    sx={{
                      fontSize: fontSize('xxl'),
                      color:
                        selectedMode === 'ask'
                          ? 'accent.secondary'
                          : 'accent.ai',
                    }}
                  />
                )}
              </SubmitButton>
            </SubmitArea>
          </InputContainer>
          <EnvRow>
            <Flex sx={{ alignItems: 'center', marginRight: 'auto' }}>
              <IntegrationsPickerButton
                enabledIntegrations={enabledIntegrations}
                onChange={onEnabledIntegrationsChange}
              />
            </Flex>
            <EnvDisplayChip
              context={context}
              onContextChange={onContextChange}
            />
          </EnvRow>
          {isCenteredEmpty && suggestions && suggestions.length > 0 && (
            <SuggestionSection
              suggestions={suggestions}
              onClick={handleSuggestionSelect}
            />
          )}
        </FooterContent>
      </Footer>
    </Wrapper>
  );
};

export default AiChat;
